import { type CSSProperties } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@helios/auth-firebase-web';
import {
  useBrandTokens,
  useSidebarState,
  useResizableSidebar,
  SIDEBAR_COLLAPSED_WIDTH,
  TopBar,
  NavIcon,
  ContactRow,
  ResizeGutter,
  SidebarToggleButton,
  orgName,
  orgLogoUrl,
  orgLogoHorizontalUrl,
  orgPhone,
  orgEmail,
} from './shared';

// ─── Settings Sidebar ───

const SETTINGS_NAV = [
  { label: 'Profile / Account', icon: 'user', path: '/' },
  { label: 'Sync Status', icon: 'refresh', path: '/clinic/sync-status' },
] as const;

function SettingsSidebar({
  currentPath,
  onNavigate,
  onSignOut,
}: {
  currentPath: string;
  onNavigate: (path: string) => void;
  onSignOut: () => void;
}) {
  return (
    <aside
      className="shell-sidebar pc-sidebar"
      style={{
        width: '100%',
        height: '100%',
        overflowX: 'clip',
        overflowY: 'auto',
        borderRadius: 'var(--radius-xl) 0 0 var(--radius-xl)',
        flexShrink: 0,
      }}
    >
      {/* Inner content — always full width, clipped when collapsed */}
      <div
        style={{
          width: '100%',
          display: 'flex',
          background: 'rgba(255,255,255,0.35)',
          backdropFilter: 'blur(40px) saturate(180%)',
          WebkitBackdropFilter: 'blur(40px) saturate(180%)',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        {/* Clinic Info Header (placeholder) */}
        <div
          style={{
            padding: 'var(--sp-6)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          {/* Logo + clinic name */}
          {orgLogoHorizontalUrl ? (
            <img
              src={orgLogoHorizontalUrl}
              alt={orgName}
              style={{
                width: '100%',
                maxHeight: 72,
                objectFit: 'contain',
                objectPosition: 'left center',
              }}
            />
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-3)',
              }}
            >
              {orgLogoUrl ? (
                <img
                  src={orgLogoUrl}
                  alt={orgName}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 'var(--radius-full)',
                    objectFit: 'cover',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 'var(--radius-full)',
                    background: 'var(--bg-surface-2)',
                    border: '1px solid var(--border)',
                    flexShrink: 0,
                  }}
                />
              )}
              <span
                style={{
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 'var(--font-weight-semi)',
                  lineHeight: 'var(--line-height-lg)',
                  letterSpacing: '-0.44px',
                  color: 'var(--text)',
                }}
              >
                {orgName}
              </span>
            </div>
          )}

          {/* Contact rows */}
          <div
            style={{
              marginTop: 'var(--sp-4)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-3)',
            }}
          >
            <ContactRow icon="map-pin" text="123 Main Street" />
            {orgPhone && <ContactRow icon="phone" text={orgPhone} />}
            {orgEmail && <ContactRow icon="mail" text={orgEmail} />}
          </div>
        </div>

        {/* Settings Navigation */}
        <div style={{ padding: 'var(--sp-6) var(--sp-6) 0' }}>
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              lineHeight: 'var(--line-height-sm)',
              letterSpacing: '0.6px',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
            }}
          >
            Settings
          </span>

          <nav
            style={{
              marginTop: 'var(--sp-4)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-1)',
            }}
          >
            {SETTINGS_NAV.map((item) => {
              const isActive = item.path != null && currentPath === item.path;
              return (
                <a
                  key={item.label}
                  href={item.path ?? '#'}
                  onClick={(e) => {
                    e.preventDefault();
                    if (item.path) onNavigate(item.path);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--sp-3)',
                    height: isActive ? 48 : 44,
                    padding: isActive ? '0 18px' : '0 16px',
                    borderRadius: 'var(--radius-lg)',
                    background: isActive ? 'var(--gradient-nav-active)' : 'transparent',
                    border: isActive ? '2px solid var(--border-strong)' : '2px solid transparent',
                    color: isActive ? 'var(--text)' : 'var(--text-muted)',
                    textDecoration: 'none',
                    fontSize: 'var(--font-size-base)',
                    fontWeight: 'var(--font-weight-medium)',
                    lineHeight: 'var(--line-height-base)',
                    letterSpacing: '-0.15px',
                    cursor: item.path ? 'pointer' : 'default',
                    opacity: item.path ? 1 : 0.5,
                  }}
                >
                  <NavIcon name={item.icon} />
                  {item.label}
                </a>
              );
            })}
          </nav>
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Sign Out */}
        <div style={{ padding: 'var(--sp-4) var(--sp-6) var(--sp-6)' }}>
          <button
            onClick={onSignOut}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-3)',
              height: 44,
              padding: '0 16px',
              borderRadius: 'var(--radius-md)',
              background: 'transparent',
              border: '2px solid transparent',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-medium)',
              lineHeight: 'var(--line-height-base)',
              letterSpacing: '-0.15px',
              cursor: 'pointer',
              width: '100%',
              textAlign: 'left',
            }}
          >
            <NavIcon name="log-out" />
            Sign Out
          </button>
        </div>
      </div>
    </aside>
  );
}

// ─── Main Layout ───

export function CommonShellLayout() {
  const { collapsed, toggle } = useSidebarState('powercareai:common-sidebar-collapsed');
  const {
    width: sbWidth,
    dragging,
    startDrag,
    resetWidth,
  } = useResizableSidebar({
    storageKey: 'powercareai:common-sidebar-width',
    min: 280,
    max: 420,
    defaultWidth: 320,
    collapsed,
    onExpand: toggle,
  });
  const brandTokens = useBrandTokens();
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();

  return (
    <div
      className="pc-desk"
      style={
        {
          ...brandTokens,
          display: 'grid',
          gridTemplateColumns: '1fr',
          gridTemplateRows: 'var(--topbar-height) 1fr',
          height: '100vh',

          fontFamily: 'var(--font-family)',
        } as CSSProperties
      }
    >
      <TopBar sidebarWidth={collapsed ? SIDEBAR_COLLAPSED_WIDTH : sbWidth} />

      {/* Main content area — right of rail, below topbar */}
      <div
        style={{
          padding: 'var(--content-inset)',
          position: 'relative',
        }}
      >
        {/* Main Card */}
        <div
          style={{
            background: 'transparent',
            borderRadius: 'var(--main-card-radius)',
            boxShadow: 'var(--shadow-paper)',
            height: '100%',
            display: 'flex',
            overflow: 'clip',
          }}
        >
          {/* Sidebar wrapper */}
          <div
            style={{
              width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : sbWidth,
              flexShrink: 0,
              transition: dragging ? 'none' : 'width 200ms ease-out',
              overflow: 'hidden',
            }}
          >
            <SettingsSidebar
              currentPath={location.pathname}
              onNavigate={(path) => navigate(path)}
              onSignOut={signOut}
            />
          </div>

          <ResizeGutter onDragStart={startDrag} onDoubleClick={resetWidth} />

          {/* Content Area — paper */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              background: '#ffffff',
              borderRadius: '0 var(--main-card-radius) var(--main-card-radius) 0',
            }}
          >
            {/* Content Title Bar — 53px per Common Mode spec */}
            <div
              style={{
                height: 'var(--content-title-height)',
                padding: '12px var(--sp-6)',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0,
                background: 'var(--bg-surface)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
                <SidebarToggleButton collapsed={collapsed} onToggle={toggle} />
                <h1
                  style={{
                    fontSize: 'var(--font-size-lg2)',
                    fontWeight: 'var(--font-weight-semi)',
                    lineHeight: 'var(--line-height-lg)',
                    letterSpacing: '-0.45px',
                    color: 'var(--text)',
                    margin: 0,
                  }}
                >
                  Settings
                </h1>
              </div>
            </div>

            {/* Scrollable Content Canvas */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <div
                className={`shell-canvas${collapsed ? ' shell-canvas--collapsed' : ''}`}
                style={{
                  width: '100%',
                  padding: 'var(--sp-8) var(--sp-6) var(--sp-6)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--sp-6)',
                }}
              >
                <Outlet />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
